'use client'

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, CheckCircle2, XCircle, Filter } from 'lucide-react'
import { QuizResult } from '@/hooks/useStudentProgress'

// ── "Quiet Academy" tokens — navy surfaces, white text, one gold accent.
//    Rose = the single semantic exception (below-standard score / wrong answer).
//    TODO: extract into a shared design-tokens module used by every component.
const C = {
  card:     '#1A2336',
  tile:     '#212D49',
  well:     'rgba(255,255,255,0.045)',
  line:     'rgba(255,255,255,0.09)',
  lineSoft: 'rgba(255,255,255,0.055)',
  text:     '#F2F5FA',
  muted:    '#A6AEC0',
  gold:     '#C9A85C',
  goldSoft: '#D4B36A',
  goldTint: 'rgba(201,168,92,0.13)',
  rose:     '#EE9EB0',
  roseTint: 'rgba(238,158,176,0.13)',
}

/** Monochrome scoring: white at/above standard, rose only below. */
function scoreTone(pct: number) {
  return pct >= 60 ? { fg: C.text, bg: C.well } : { fg: C.rose, bg: C.roseTint }
}

function ScorePill({ pct }: { pct: number }) {
  const tone = scoreTone(pct)
  return (
    <span style={{
      fontSize: 13, fontWeight: 800,
      color: tone.fg, background: tone.bg,
      padding: '3px 12px', borderRadius: 20,
      display: 'inline-block', minWidth: 52, textAlign: 'center',
      fontVariantNumeric: 'tabular-nums',
    }}>
      {Math.round(pct)}%
    </span>
  )
}

function QuizDetailRow({ result }: { result: QuizResult }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const correct = Array.isArray(result.questions) ? result.questions.filter(q => q.correct_yn).length : 0
  const total = Array.isArray(result.questions) ? result.questions.length : 0
  const durationMin = result.duration_seconds ? Math.round(result.duration_seconds / 60) : null

  return (
    <>
      <motion.tr
        whileHover={{ background: 'rgba(255,255,255,0.04)' }}
        style={{ cursor: 'pointer', transition: 'background 0.1s' }}
        onClick={() => setOpen(o => !o)}
      >
        <td style={{ padding: '14px 16px', fontSize: 13, color: C.muted, whiteSpace: 'nowrap' }}>
          {new Date(result.attempted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        </td>
        <td style={{ padding: '14px 16px', fontSize: 13, color: C.text, fontWeight: 600 }}>
          {result.category_name || t('education.quizTable.columns.subject')}
        </td>
        <td style={{ padding: '14px 16px', fontSize: 13, color: C.muted, whiteSpace: 'nowrap' }}>
          U{result.lesson_unit} · L{result.lesson_number}
        </td>
        <td style={{ padding: '14px 16px' }}>
          <ScorePill pct={result.score_percentage} />
        </td>
        <td style={{ padding: '14px 16px', color: C.muted }}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </td>
      </motion.tr>

      <AnimatePresence>
        {open && (
          <tr key="detail">
            <td colSpan={5} style={{ padding: 0, border: 'none' }}>
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ overflow: 'hidden' }}
              >
                <div style={{ padding: '16px 20px 20px', background: C.well, borderBottom: `1px solid ${C.lineSoft}` }}>
                  <p style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, margin: '0 0 12px' }}>
                    {t('education.quizTable.detail.correctOf', { correct, total })}
                    {durationMin !== null && ` · ${t('education.quizTable.detail.duration', { minutes: durationMin })}`}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(result.questions || []).map((q, qi) => (
                      <div key={qi} style={{
                        background: C.card, borderRadius: 12, padding: '12px 16px',
                        border: `1px solid ${C.lineSoft}`,
                        borderLeft: `4px solid ${q.correct_yn ? C.gold : C.rose}`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                          <div style={{ color: q.correct_yn ? C.gold : C.rose, flexShrink: 0, marginTop: 1 }}>
                            {q.correct_yn ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                          </div>
                          <p style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: 0 }}>{q.question}</p>
                        </div>
                        <div style={{ display: 'flex', gap: 8, paddingLeft: 24, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 8, background: C.goldTint, color: C.goldSoft, fontWeight: 600 }}>
                            ✓ {q.correct}
                          </span>
                          {!q.correct_yn && (
                            <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 8, background: C.roseTint, color: C.rose, fontWeight: 600 }}>
                              ✗ {q.student_answer || '—'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  )
}

interface QuizResultsTableProps {
  results: QuizResult[]
  subjectNames: string[]
}

export default function QuizResultsTable({ results, subjectNames }: QuizResultsTableProps) {
  const { t } = useTranslation()
  const [filterSubject, setFilterSubject] = useState<string>('all')

  const filtered = filterSubject === 'all' ? results : results.filter(r => r.category_name === filterSubject)

  const columns = [
    t('education.quizTable.columns.date'),
    t('education.quizTable.columns.subject'),
    t('education.quizTable.columns.position'),
    t('education.quizTable.columns.score'),
    '',
  ]

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <Filter size={16} color={C.muted} />
        <span style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>{t('education.quizTable.filterLabel')}</span>
        <select
          id="edu-quiz-filter"
          value={filterSubject}
          onChange={e => setFilterSubject(e.target.value)}
          style={{
            fontSize: 13, padding: '6px 12px', borderRadius: 10,
            border: `1px solid ${C.line}`, outline: 'none',
            background: C.tile, color: C.text, cursor: 'pointer',
            colorScheme: 'dark', fontFamily: 'inherit',
          }}
        >
          <option value="all">{t('education.quizTable.filterAll')}</option>
          {subjectNames.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
        {filtered.length !== results.length && (
          <button
            onClick={() => setFilterSubject('all')}
            style={{
              fontSize: 12, color: C.goldSoft, background: C.goldTint,
              border: 'none', borderRadius: 8, padding: '4px 10px',
              cursor: 'pointer', fontWeight: 700,
            }}
          >
            {t('education.quizTable.clearFilter')}
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', background: C.card, borderRadius: 16, border: `1px solid ${C.line}` }}>
          <p style={{ color: C.muted, margin: 0 }}>{t('education.quizTable.empty')}</p>
        </div>
      ) : (
        <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.line}`, overflow: 'hidden', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.tile, borderBottom: `1px solid ${C.line}` }}>
                {columns.map((h, i) => (
                  <th key={i} style={{
                    padding: '12px 16px', textAlign: 'left',
                    fontSize: 11, fontWeight: 800,
                    textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted,
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((result, i) => (
                <QuizDetailRow key={result.id || i} result={result} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}